import { NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon, MatIconRegistry } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer } from '@angular/platform-browser';
import { Router, RouterLink } from '@angular/router';
import { AppType, ENVIRONMENT, Environment, LoginModel } from '@domains';
import { encryptKey, LocalStorageService } from '@rspl-api';
import {
  CardComponent,
  Destructible,
  DisableDoubleClickDirective,
  FxFlexDirective,
  FxLayoutAlignDirective,
  FxLayoutDirective,
  FxLayoutGapDirective,
} from '@rspl-ui';
import { LaddaModule } from 'angular2-ladda';
import * as CryptoJS from 'crypto-js';
import { filter, finalize, take } from 'rxjs/operators';

import { AFTER_LOGIN, AuthenticationService } from '../../authentication.service';

@Component({
  selector: 'rspl-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  imports: [
    NgTemplateOutlet,
    FxLayoutDirective,
    FxLayoutAlignDirective,
    FxLayoutGapDirective,
    FormsModule,
    ReactiveFormsModule,
    NgClass,
    MatIconButton,
    MatSuffix,
    MatIcon,
    LaddaModule,
    CardComponent,
    FxFlexDirective,
    MatFormField,
    MatLabel,
    MatInput,
    MatButton,
    DisableDoubleClickDirective,
    RouterLink,
  ],
})
export class LoginComponent extends Destructible implements OnInit {
  public form?: FormGroup<{
    email: FormControl<string | null>;
    password: FormControl<string | null>;
  }>;
  public app: AppType;
  public resetPasswordUrl: string;
  appTypes = AppType;
  isTPL: boolean;
  isDriver: boolean;
  support?: string;
  showPassword = false;
  isSubmitting = false;
  showErrors = false;

  constructor(
    iconRegistry: MatIconRegistry,
    sanitizer: DomSanitizer,
    private authService: AuthenticationService,
    private snackBar: MatSnackBar,
    private router: Router,
    @Inject(ENVIRONMENT) private config: Environment,
    private localStorage: LocalStorageService,
  ) {
    super();
    this.app = this.config.app === AppType.ZENDESK ? AppType.CAPTAIN : this.config.app;
    this.isTPL = this.config.app === AppType.TPL;
    this.isDriver = this.config.app === AppType.DRIVER;
    this.support = this.config.supportNumber;
    this.resetPasswordUrl = this.config.urls.resetPasswordUrl;

    iconRegistry.addSvgIcon('rspl-logo', sanitizer.bypassSecurityTrustResourceUrl('assets/images/new_logo_short.svg'));
  }

  ngOnInit(): void {
    const afterLogin = this.localStorage.getItem(AFTER_LOGIN);
    const shouldSkipReload = !!afterLogin; // Skip reload if we have a redirect target

    if (!this.localStorage.getItem('LOGIN_FRESH') && !shouldSkipReload) {
      this.localStorage.setItem('LOGIN_FRESH', 'true');
      window.location.reload();
      return;
    }

    this.localStorage.removeItem('LOGIN_FRESH');

    document.getElementById('app-page-loader')?.remove();
    sessionStorage.clear();

    // Only clear localStorage if user is not already logged in
    const userData = this.localStorage.getItem('rspl-user');
    if (!userData) {
      this.localStorage.clear();
      if (afterLogin) {
        this.localStorage.setItem(AFTER_LOGIN, afterLogin);
      }
    }
    this.initForm();
  }

  public login(): void {
    this.form?.updateValueAndValidity();
    this.form?.markAllAsTouched();
    this.showErrors = !!this.form?.invalid;
    if (this.showErrors) {
      return;
    }
    const formValue = this.form?.getRawValue();
    if (!formValue?.password || !formValue.email) return;
    this.isSubmitting = true;
    const user = new LoginModel({
      email: formValue?.email,
      password: formValue?.password,
    });
    this.authService
      .login(user)
      .pipe(
        finalize(() => (this.isSubmitting = false)),
        take(1),
      )
      .subscribe({
        next: () => {
          const afterLoginLocalStorage = this.localStorage.getItem(AFTER_LOGIN);

          let targetPath = this.config.loginRedirectionRoute;
          let queryParams: any = {};

          if (this.config.dynamicAfterLoginRedirect && afterLoginLocalStorage) {
            try {
              // Decrypt if production
              const decryptedUrl =
                this.config.production && afterLoginLocalStorage.includes('U2FsdGVk')
                  ? this.decryptUrl(afterLoginLocalStorage)
                  : afterLoginLocalStorage;

              if (decryptedUrl && decryptedUrl !== '/' && !decryptedUrl.includes('login')) {
                const parts = decryptedUrl.split('?');
                const afterLoginUrl = parts[0];
                const params = parts.length > 1 ? parts[1].split('&') : undefined;

                queryParams =
                  params?.reduce((a, v) => {
                    const p = v.split('=');
                    return { ...a, [p[0]]: decodeURIComponent(p[1] || '') };
                  }, {}) || {};

                const afterLogin = afterLoginUrl.split('/').filter((x) => x.trim().length > 0);

                if (afterLogin.length > 0) {
                  targetPath = afterLogin[0] === '' ? afterLogin.slice(1) : afterLogin;
                  if (targetPath.length === 0) {
                    targetPath = this.config.loginRedirectionRoute;
                  } else {
                    targetPath = ['/', ...targetPath];
                  }
                }
              }
            } catch (error) {
              // Fallback to default on error
            }
          }

          // Wait for auth state to be fully established before navigating
          this.authService.isLoggedIn$
            .pipe(
              filter((isLoggedIn) => isLoggedIn === true),
              take(1),
            )
            .subscribe(() => {
              // Small delay to ensure all state is synchronized
              setTimeout(() => {
                this.router.navigate(targetPath, { queryParams: queryParams }).then((success) => {
                  if (success) {
                    // Clean up AFTER_LOGIN only after successful navigation
                    this.localStorage.removeItem(AFTER_LOGIN);
                  }
                });
              }, 100);
            });
        },
        error: () => this.handleError(),
      });
  }

  private initForm(): void {
    this.form = new FormGroup({
      email: new FormControl('', [Validators.required, Validators.email]),
      password: new FormControl('', [Validators.required]),
    });
  }

  handleError() {
    this.snackBar.open('Access Not Allowed', 'x', {
      duration: 5000,
      panelClass: 'error',
    });
  }

  get email(): FormControl<string> {
    return this.form?.get('email') as FormControl<string>;
  }

  get password(): FormControl<string> {
    return this.form?.get('password') as FormControl<string>;
  }

  private decryptUrl(encryptedUrl: string): string {
    try {
      const decrypted = CryptoJS.AES.decrypt(encryptedUrl, encryptKey, {
        keySize: 128 / 8,
        iv: encryptKey,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      });
      return decrypted.toString(CryptoJS.enc.Utf8);
    } catch (error) {
      console.warn('[LOGIN] Failed to decrypt URL:', error);
      return '';
    }
  }
}
